import React from 'react'
import {Card, Text} from 'wix-ui-tpa/cssVars'
import {useTranslation} from '@wix/yoshi-flow-editor'
import {Experience} from '@wix/ambassador-table-reservations-v1-experience/types'
import {useSettings} from '@wix/tpa-settings/react'

import {withMediaStorageUrl} from '../../../../utils/images-gallery'
import {LayoutSize} from '../../../../components-shared/LayoutSizeProvider/types'
import {convertMinutesToHoursAndMinutes} from '../../../../utils/date'
import {ExperienceSchedule} from '../../../../components-shared/ExperienceSchedule'
import {DEFAULT_CURRENCY} from '../../../../utils/constants'
import {ReserveButton} from '../ReserveButton'
import {ExperiencePrice} from '../../../../components-shared/ExperiencePrice'
import settingsParams from '../../settingsParams'
import {headerGlobalButtonStyleParams} from '../../Settings/Tabs/Design/Buttons/stylesParams'

import {classes, st} from './Header.st.css'

interface Props {
  experience?: Experience
  onReserveClick: () => void
  regionalSettings?: string
  layoutSize: LayoutSize
  showReserveButton: boolean
  isManualApproval: boolean
}

export const Header = ({
  experience,
  onReserveClick,
  regionalSettings,
  layoutSize,
  showReserveButton,
  isManualApproval,
}: Props) => {
  const settings = useSettings()
  const showImage: boolean = settings.get(settingsParams.showImage)
  const showSummary: boolean = settings.get(settingsParams.showSummary)
  const showDuration: boolean = settings.get(settingsParams.showDuration)
  const showDate: boolean = settings.get(settingsParams.showDate)
  const showPrice: boolean = settings.get(settingsParams.showPrice)
  const showCapacity: boolean = settings.get(settingsParams.showCapacity)
  const reserveNowButtonText: string = settings.get(settingsParams.reserveNowButtonText)
  const requestNowButtonText: string = settings.get(settingsParams.requestNowButtonText)

  const {t} = useTranslation()
  const imageId = experience?.configuration?.displayInfo?.coverImage?.id
  const imageUrl = imageId ? withMediaStorageUrl(imageId) : undefined
  const shouldShowImage = imageUrl && showImage

  const maxGuests = experience?.configuration?.onlineReservations?.maxGuests?.number

  const businessSchedule = experience?.configuration?.onlineReservations?.businessSchedule

  const currency = experience?.currency ?? DEFAULT_CURRENCY

  return (
    <Card className={st(classes.root, {noImage: !shouldShowImage, [layoutSize]: true})}>
      <Card.Container className={st(classes.headerContainer)}>
        {shouldShowImage ? (
          <div className={st(classes.headerItem, classes.imageContainer)}>
            <img
              className={st(classes.experienceImage)}
              src={imageUrl}
              alt={experience?.configuration?.displayInfo?.coverImage?.altText || ''}
            />
          </div>
        ) : null}
        <div className={st(classes.headerItem, classes.headerContent)}>
          <div>
            <Text className={st(classes.experienceName)} tagName="h1">
              {experience?.configuration?.displayInfo?.name}
            </Text>
            {showSummary && (
              <Text className={st(classes.experienceSummary)} tagName="p">
                {experience?.configuration?.displayInfo?.shortDescription}
              </Text>
            )}
          </div>
          <div className={st(classes.detailsTableContainer)}>
            <div className={st(classes.detailsTable, {oneRow: !shouldShowImage})}>
              {businessSchedule && showDuration && (
                <Text tagName="p" className={st(classes.detailsCell)}>
                  {t(
                    'uou-reservations.experiences-list.duration',
                    convertMinutesToHoursAndMinutes(businessSchedule.durationInMinutes ?? 0),
                  )}
                </Text>
              )}
              {businessSchedule && showDate && (
                <ExperienceSchedule
                  businessSchedule={businessSchedule}
                  regionalSettings={regionalSettings}
                  className={st(classes.detailsCell)}
                />
              )}

              {showPrice && (
                <ExperiencePrice
                  className={st(classes.detailsCell)}
                  currency={currency}
                  paymentPolicy={experience?.configuration?.paymentPolicy}
                />
              )}
              {maxGuests && showCapacity && (
                <Text tagName="p" className={st(classes.detailsCell)}>
                  {t('uou-experiences.experience-details.party-size.guests-max', {
                    number: maxGuests,
                  })}
                </Text>
              )}
            </div>
          </div>
          {showReserveButton && (
            <ReserveButton
              onClick={onReserveClick}
              className={st(classes.reserveButton)}
              type="button"
              globalStyleParams={headerGlobalButtonStyleParams}
            >
              {isManualApproval ? requestNowButtonText : reserveNowButtonText}
            </ReserveButton>
          )}
        </div>
      </Card.Container>
    </Card>
  )
}
